// Questions Module Utility Functions
import { USER_ROLES, PERMISSIONS } from '../constants';

/**
 * Format date to relative time (e.g., "2h ago", "3d ago")
 */
export const formatRelativeDate = (dateString) => {
  if (!dateString) return 'Unknown';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInHours < 1) {
    const minutes = Math.floor(diffInMs / (1000 * 60));
    return minutes < 1 ? 'Just now' : `${minutes}m ago`;
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours}h ago`;
  } else if (diffInDays < 7) {
    const days = Math.floor(diffInDays);
    return `${days}d ago`;
  } else {
    const day = date.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }
};

/**
 * Format date to simple date string (e.g., "5 Nov 2025")
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Unknown';
    
    const day = date.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  } catch {
    return 'Unknown';
  }
};

/**
 * Format date to full date string with time
 */
export const formatFullDate = (dateString) => {
  if (!dateString) return 'Unknown';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Unknown';
    
    const day = date.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  } catch {
    return 'Unknown';
  }
};

/**
 * Get status configuration
 */
export const getStatusConfig = (status, STATUS_CONFIG) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG.open;
};

/**
 * Get priority configuration
 */
export const getPriorityConfig = (priority, PRIORITY_CONFIG) => {
  return PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
};

/**
 * Get module configuration
 */
export const getModuleConfig = (module, MODULE_CONFIG) => {
  return MODULE_CONFIG[module] || MODULE_CONFIG.general;
};

/**
 * Check if user has permission
 */
export const hasPermission = (userRole, permission) => {
  const rolePermissions = {
    [USER_ROLES.SUPER_ADMIN]: [PERMISSIONS.CREATE, PERMISSIONS.MODERATE, PERMISSIONS.ADMIN],
    [USER_ROLES.CREATOR]: [PERMISSIONS.CREATE, PERMISSIONS.MODERATE],
    [USER_ROLES.VIEWER]: []
  };
  
  return rolePermissions[userRole]?.includes(permission) || false;
};

/**
 * Generate question ID
 */
export const generateQuestionId = () => {
  return `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validate question data
 */
export const validateQuestion = (questionData) => {
  const errors = [];
  
  if (!questionData.title?.trim()) {
    errors.push('Title is required');
  }
  
  if (!questionData.description?.trim()) {
    errors.push('Description is required');
  }
  
  if (questionData.title?.length > 200) {
    errors.push('Title must be less than 200 characters');
  }
  
  if (questionData.description?.length > 5000) {
    errors.push('Description must be less than 5000 characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file extension
 */
export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

/**
 * Check if file type is allowed
 */
export const isAllowedFileType = (filename, allowedTypes = ['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png', 'gif']) => {
  const extension = getFileExtension(filename).toLowerCase();
  return allowedTypes.includes(extension);
};

/**
 * Truncate text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate random color
 */
export const generateRandomColor = () => {
  const colors = [
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-yellow-100 text-yellow-700',
    'bg-red-100 text-red-700',
    'bg-purple-100 text-purple-700',
    'bg-pink-100 text-pink-700',
    'bg-indigo-100 text-indigo-700'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

/**
 * Sort questions by different criteria
 */
export const sortQuestions = (questions, sortBy = 'created_at', sortOrder = 'desc') => {
  return [...questions].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    // Handle date sorting
    if (sortBy === 'created_at' || sortBy === 'updated_at') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    
    // Handle string sorting
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });
};

/**
 * Filter questions by criteria
 */
export const filterQuestions = (questions, filters) => {
  return questions.filter(question => {
    // Status filter
    if (filters.status && question.status !== filters.status) {
      return false;
    }
    
    // Priority filter
    if (filters.priority && question.priority !== filters.priority) {
      return false;
    }
    
    // Category filter
    if (filters.category && question.category !== filters.category) {
      return false;
    }
    
    // Module filter
    if (filters.module && question.module_type !== filters.module) {
      return false;
    }
    
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const searchableText = [
        question.title,
        question.description,
        question.user_name,
        question.category
      ].join(' ').toLowerCase();
      
      if (!searchableText.includes(searchTerm)) {
        return false;
      }
    }
    
    return true;
  });
};
